<template>
  <div class="my_suggest info-setting">
    <div class="profile-card">
      <div class="profile-banner">
        <AvatarCropUploader :editable="isEditing" :avatar-url="avatarDisplayUrl" :sex="form.sex"
          @uploaded="onAvatarUploaded" />
        <div class="banner-main">
          <div class="banner-title-row">
            <h2 class="title">基本信息设置</h2>
            <div class="role-tags">
              <span v-for="(label, idx) in roleLabels" :key="idx" class="role-tag">{{ label }}</span>
            </div>
          </div>
          <div v-if="showIntroduce" class="intro-block">
            <span class="intro-label">个人简介：</span>
            <el-input v-if="isEditing" type="textarea" v-model="form.introduce" :rows="3" resize="none" maxlength="250"
              show-word-limit placeholder="请输入个人简介" />
            <div v-else class="intro-readonly">{{ displayOrEmpty(form.introduce) }}</div>
          </div>
        </div>
        <div class="banner-actions">
          <template v-if="!isEditing">
            <el-button type="primary" size="small" @click="enterEdit">修改信息</el-button>
          </template>
          <template v-else>
            <el-button size="small" @click="cancelEdit">取消</el-button>
            <el-button type="primary" size="small" :loading="saving" @click="onSubmit">保存</el-button>
          </template>
        </div>
      </div>

      <div class="form-section">
        <el-form ref="form" :model="form" label-width="96px" :rules="rules">
          <el-row :gutter="20">
            <el-col v-for="field in visibleFields" :key="field.key" :span="12" class="field-col">
              <el-form-item :label="field.label + '：'" :prop="field.prop">
                <!-- 查看 / 字典待补：只读 -->
                <div v-if="!isEditing || field.pendingDict" class="readonly-box">{{ viewText(field) }}</div>

                <!-- 姓名 -->
                <el-input v-else-if="field.key === 'name'" v-model="form.name" maxlength="30" show-word-limit />
                <!-- 学籍号/学号（后端字段 studentCode） -->
                <el-input
                  v-else-if="field.key === 'studyNumber'"
                  v-model="form.studyNumber"
                  @input="onStudyNumberInput"
                />
                <!-- 身份证 -->
                <el-input v-else-if="field.key === 'idCard'" v-model="form.idCard" maxlength="18" />
                <!-- 性别 -->
                <el-radio-group v-else-if="field.key === 'sex'" v-model="form.sex">
                  <el-radio :label="1">男</el-radio>
                  <el-radio :label="0">女</el-radio>
                </el-radio-group>
                <!-- 手机 -->
                <el-input v-else-if="field.key === 'phone'" v-model="form.phone" />
                <!-- 邮箱 -->
                <el-input v-else-if="field.key === 'email'" v-model="form.email" />
                <!-- 地区 -->
                <el-cascader v-else-if="field.key === 'area'" v-model="form.areaPath" :props="cascaderProps" filterable
                  clearable placeholder="请选择地区" popper-class="profile-select-dropdown" style="width: 100%"
                  @change="onAreaChange" />
                <!-- 学校 -->
                <el-select v-else-if="field.key === 'school'" v-model="form.schoolId" filterable remote clearable
                  :disabled="!hasArea" :remote-method="remoteSchool" :loading="schoolLoading" placeholder="请选择或搜索学校"
                  popper-class="profile-select-dropdown" style="width: 100%" @change="onSchoolChange">
                  <el-option v-for="item in schoolList" :key="item.id" :label="item.name" :value="item.id" />
                </el-select>
                <!-- 学段 -->
                <el-select v-else-if="field.key === 'stage'" v-model="form.stageName" :disabled="!form.schoolId"
                  clearable placeholder="请选择学段" popper-class="profile-select-dropdown" style="width: 100%"
                  @change="onStageChange">
                  <el-option v-for="item in stageOptions" :key="item.id" :label="item.name" :value="item.name" />
                </el-select>
                <!-- 班级 -->
                <el-select v-else-if="field.key === 'clazz'" v-model="form.classId" filterable clearable
                  :disabled="!form.schoolId" placeholder="请选择班级" popper-class="profile-select-dropdown"
                  style="width: 100%" @change="onClassChange">
                  <el-option v-for="item in clazzList" :key="item.id" :label="item.name" :value="item.id" />
                </el-select>
                <!-- 年级：编辑填入学年份 -->
                <el-input v-else-if="field.key === 'grade'" v-model="form.enterYear" placeholder="请输入入学年份，如 2023"
                  maxlength="4" />
                <!-- 学科 -->
                <el-select v-else-if="field.key === 'subject'" v-model="form.subjectId" filterable clearable
                  placeholder="请选择学科" popper-class="profile-select-dropdown" style="width: 100%"
                  @change="onSubjectChange">
                  <el-option v-for="item in subjectList" :key="item.id" :label="item.name" :value="item.id" />
                </el-select>
                <!-- 职称 -->
                <el-select v-else-if="field.key === 'title'" v-model="form.titleId" filterable clearable
                  placeholder="请选择职称" popper-class="profile-select-dropdown" style="width: 100%"
                  @change="onTitleChange">
                  <el-option v-for="item in titleList" :key="item.id" :label="item.name" :value="item.id" />
                </el-select>
                <!-- 任教教龄 -->
                <el-input v-else-if="field.key === 'experience'" v-model="form.experience" maxlength="2"
                  placeholder="0-99" @input="onExperienceInput" />
              </el-form-item>
            </el-col>
          </el-row>
        </el-form>
      </div>
    </div>
  </div>
</template>

<script>
  import AvatarCropUploader from './components/AvatarCropUploader.vue';
  import {
    normalizeUserRoles,
    getRoleLabels,
    getVisibleFieldsForRoles,
    shouldShowIntroduce,
    formatGradeWithEnterYear,
    formatClassDisplay,
    formatAreaDisplay,
    parseAreaPathFromForm,
    displayOrEmpty,
    normalizeProfileForm,
    buildFixInfoPayload,
    getChangedIdentityFields,
    isUserExistResponse,
    STAGE_OPTIONS,
  } from './profile-field-utils';

  const unwrapList = (res) => {
    if (Array.isArray(res)) return res;
    const data = res && res.data;
    if (Array.isArray(data)) return data;
    if (data && Array.isArray(data.list)) return data.list;
    // query_school：列表在 data.school
    if (data && Array.isArray(data.school)) return data.school;
    if (data && Array.isArray(data.records)) return data.records;
    return [];
  };

  const pickId = (item) => item.id ?? item.schoolId ?? item.clazzId ?? item.classId ?? item.value;
  const pickName = (item) => item.name || item.schoolName || item.clazzName || item.className || item.label || '';

  export default {
    components: { AvatarCropUploader },
    data() {
      const validateIDcard = (rule, value, callback) => {
        if (value === '' || value == null) return callback();
        const ok = /(^\d{15}$)|(^\d{18}$)|(^\d{17}(\d|X|x)$)/.test(value);
        if (!ok) return callback(new Error('请输入正确的身份证号码'));
        callback();
      };
      const validatePhone = (rule, value, callback) => {
        const val = value == null ? '' : String(value);
        if (val === '') return callback(new Error('请输入手机号'));
        if (!/^1[3-9]\d{9}$/.test(val)) return callback(new Error('请输入正确的手机号码!'));
        callback();
      };
      const validateEmail = (rule, value, callback) => {
        if (value === '' || value == null) return callback();
        if (String(value).indexOf('@') < 0) return callback(new Error('请输入格式正确的邮箱地址!'));
        callback();
      };
      const validateExperience = (rule, value, callback) => {
        if (value === '' || value == null) return callback();
        if (!/^\d{1,2}$/.test(String(value))) return callback(new Error('任教教龄须为 0–99 的纯数字'));
        const n = Number(value);
        if (n < 0 || n > 99) return callback(new Error('任教教龄须为 0–99 的纯数字'));
        callback();
      };
      const that = this;
      return {
        isEditing: false,
        saving: false,
        form: {},
        editSnapshot: null,
        titleList: [],
        subjectList: [],
        schoolList: [],
        clazzList: [],
        schoolLoading: false,
        stageOptions: STAGE_OPTIONS.slice(),
        cascaderProps: {
          lazy: true,
          value: 'value',
          label: 'label',
          leaf: 'leaf',
          lazyLoad(node, resolve) {
            that.loadRegionNodes(node, resolve);
          },
        },
        rules: {
          name: [{ required: true, message: '请输入姓名', trigger: 'blur' }],
          phone: [{ validator: validatePhone, trigger: 'blur' }],
          idCard: [{ validator: validateIDcard, trigger: 'blur' }],
          email: [{ validator: validateEmail, trigger: 'blur' }],
          experience: [{ validator: validateExperience, trigger: 'blur' }],
        },
      };
    },
    computed: {
      roles() {
        return normalizeUserRoles(
          this.$store.state.user.userRoleList,
          this.$store.state.user.role
        );
      },
      roleLabels() {
        return getRoleLabels(this.roles);
      },
      visibleFields() {
        return getVisibleFieldsForRoles(this.roles);
      },
      showIntroduce() {
        return shouldShowIntroduce(this.roles);
      },
      hasGradeField() {
        return this.visibleFields.some((f) => f.key === 'grade');
      },
      hasArea() {
        return Array.isArray(this.form.areaPath) && this.form.areaPath.length > 0;
      },
      avatarDisplayUrl() {
        if (!this.form.avatar) return '';
        const base = `${this.downloadURL}${this.form.avatar}`;
        return this.form._avatarTs ? `${base}&_t=${this.form._avatarTs}` : base;
      },
    },
    methods: {
      displayOrEmpty,
      viewText(field) {
        const form = this.form;
        switch (field.key) {
          case 'sex':
            if (form.sex === 1 || form.sex === '1') return '男';
            if (form.sex === 0 || form.sex === '0') return '女';
            return '无';
          case 'area':
            return displayOrEmpty(formatAreaDisplay(form));
          case 'school':
            return displayOrEmpty(form.schoolName);
          case 'stage':
            return displayOrEmpty(form.stageName);
          case 'clazz': {
            const text = formatClassDisplay({
              className: form.clazzName || form.className,
              enterYear: this.findClazzEnterYear(form.classId),
              formEnterYear: form.enterYear,
              hasGradeField: this.hasGradeField,
              gradeValue: form.enterYear || form.gradeName,
              roles: this.roles,
            });
            return displayOrEmpty(text);
          }
          case 'grade':
            return displayOrEmpty(
              formatGradeWithEnterYear(form.enterYear, form.gradeName, this.roles)
            );
          case 'subject':
            return displayOrEmpty(form.subjectName);
          case 'title':
            return displayOrEmpty(form.titleName);
          default:
            return displayOrEmpty(form[field.prop] != null ? form[field.prop] : form[field.key]);
        }
      },
      findClazzEnterYear(classId) {
        const item = this.clazzList.find((c) => String(c.id) === String(classId));
        return item && (item.enterYear || item.enrollYear);
      },
      unwrapUser(res) {
        if (!res) return {};
        if (res.data && typeof res.data === 'object' && !Array.isArray(res.data) && res.data.id) {
          return res.data;
        }
        if (res.id) return res;
        return (res.data && typeof res.data === 'object' && res.data.data) || res.data || res;
      },
      isOk(res) {
        if (!res) return false;
        if (res.code === 200 || res.code === 0) return true;
        if (res.data && (res.data.code === 200 || res.data.code === 0)) return true;
        const msg = (res && res.msg) || (res.data && res.data.msg) || '';
        return msg === '操作成功' || msg === '请求成功';
      },
      pickAreaValue(item) {
        return item.id ?? item.value ?? item.code ?? item.regionCode;
      },
      pickAreaLabel(item) {
        return item.name || item.label || item.regionName || String(this.pickAreaValue(item));
      },
      async loadRegionNodes(node, resolve) {
        try {
          const params = node.level === 0 ? {} : { parentId: node.value };
          const res = await this.$Api.My.queryRegions(params);
          const list = unwrapList(res);
          const nodes = list.map((item) => {
            const value = this.pickAreaValue(item);
            const label = this.pickAreaLabel(item);
            const hasNested = item.children && item.children.length > 0;
            let leaf = false;
            if (item.leaf === true || item.isLeaf === true) leaf = true;
            else if (hasNested) leaf = false;
            else if (item.hasChildren === false) leaf = true;
            else leaf = node.level >= 2;
            return { value, label, leaf };
          });
          resolve(nodes);
        } catch (e) {
          console.warn('query_regions 暂不可用', e);
          resolve([]);
        }
      },
      areaQueryParams(keyword) {
        const path = this.form.areaPath || [];
        const params = {
          province: path[0],
          city: path[1],
          area: path[2],
          areaId: path[path.length - 1],
        };
        if (keyword) params.keyword = keyword;
        return params;
      },
      async fetchSchools(keyword) {
        if (!this.hasArea) {
          this.schoolList = [];
          return;
        }
        this.schoolLoading = true;
        try {
          const res = await this.$Api.My.querySchool(this.areaQueryParams(keyword));
          const list = unwrapList(res).map((item) => ({
            id: pickId(item),
            name: pickName(item),
          }));
          this.ensureCurrentSchool(list);
          this.schoolList = list;
        } catch (e) {
          console.warn('query_school 暂不可用', e);
          const list = [];
          this.ensureCurrentSchool(list);
          this.schoolList = list;
        } finally {
          this.schoolLoading = false;
        }
      },
      ensureCurrentSchool(list) {
        if (this.form.schoolId == null || this.form.schoolId === '') return;
        const exists = list.some((s) => String(s.id) === String(this.form.schoolId));
        if (!exists && this.form.schoolName) {
          list.unshift({ id: this.form.schoolId, name: this.form.schoolName });
        }
      },
      remoteSchool(query) {
        this.fetchSchools(query);
      },
      async fetchClazz() {
        if (!this.form.schoolId) {
          this.clazzList = [];
          return;
        }
        try {
          const res = await this.$Api.My.queryClazz({ schoolId: this.form.schoolId });
          const list = unwrapList(res).map((item) => ({
            id: pickId(item),
            name: pickName(item),
            enterYear: item.enterYear || item.enrollYear,
          }));
          this.ensureCurrentClazz(list);
          this.clazzList = list;
        } catch (e) {
          console.warn('query_clazz 暂不可用', e);
          const list = [];
          this.ensureCurrentClazz(list);
          this.clazzList = list;
        }
      },
      ensureCurrentClazz(list) {
        if (this.form.classId == null || this.form.classId === '') return;
        const exists = list.some((c) => String(c.id) === String(this.form.classId));
        if (!exists && (this.form.clazzName || this.form.className)) {
          list.unshift({
            id: this.form.classId,
            name: this.form.clazzName || this.form.className,
            enterYear: this.form.enterYear,
          });
        }
      },
      onAreaChange(val) {
        this.form.schoolId = '';
        this.form.schoolName = '';
        this.clearClassAndStage();
        this.schoolList = [];
        if (val && val.length) {
          this.form.province = val[0];
          this.form.city = val[1];
          this.form.areaId = val[val.length - 1];
          this.fetchSchools();
        } else {
          this.form.province = '';
          this.form.city = '';
          this.form.areaId = '';
          this.form.areaName = '';
        }
      },
      onSchoolChange(id) {
        this.clearClassAndStage();
        const item = this.schoolList.find((s) => String(s.id) === String(id));
        this.form.schoolName = item ? item.name : this.form.schoolName;
        if (id) this.fetchClazz();
        else this.clazzList = [];
      },
      clearClassAndStage() {
        this.form.classId = '';
        this.form.clazzName = '';
        this.form.className = '';
        this.form.stageName = '';
        this.form.stageId = '';
        this.form.enterYear = '';
      },
      onClassChange(id) {
        const item = this.clazzList.find((c) => String(c.id) === String(id));
        if (!item) {
          this.form.clazzName = '';
          this.form.className = '';
          return;
        }
        this.form.classId = item.id;
        this.form.clazzName = item.name;
        this.form.className = item.name;
        // 入学年来自班级，仅用于展示/年级推算，保存时不单独提交
        if (item.enterYear) this.form.enterYear = item.enterYear;
      },
      onStageChange(name) {
        // 学段本期不落库，仅前端展示
        const item = this.stageOptions.find((s) => s.name === name);
        this.form.stageId = item ? item.id : '';
      },
      onSubjectChange(id) {
        const item = this.subjectList.find((s) => s.id === id);
        this.form.subjectName = item ? item.name : '';
      },
      onTitleChange(id) {
        const item = this.titleList.find((s) => s.id === id);
        this.form.titleName = item ? item.name : '';
      },
      onExperienceInput(val) {
        const next = String(val || '').replace(/\D/g, '').slice(0, 2);
        this.form.experience = next;
      },
      onStudyNumberInput(val) {
        this.form.studyNumber = val;
        this.form.studentCode = val;
      },
      onAvatarUploaded(md5) {
        this.form.avatar = md5;
        this.form._avatarTs = Date.now();
      },
      syncFormBeforeSave() {
        const path = this.form.areaPath || [];
        if (path.length) {
          this.form.province = path[0];
          this.form.city = path[1];
          this.form.areaId = path[path.length - 1];
        }
        const cascaderComp = this.findCascader(this);
        if (cascaderComp && cascaderComp.getCheckedNodes) {
          const nodes = cascaderComp.getCheckedNodes();
          if (nodes && nodes[0]) {
            const labels = [];
            let n = nodes[0];
            while (n && n.level) {
              labels.unshift(n.label);
              n = n.parent;
            }
            if (labels.length) this.form.areaName = labels.join('/');
          }
        }
        if (!this.form.areaName) this.form.areaName = formatAreaDisplay(this.form);
        const school = this.schoolList.find((s) => String(s.id) === String(this.form.schoolId));
        if (school) this.form.schoolName = school.name;
        const clazz = this.clazzList.find((c) => String(c.id) === String(this.form.classId));
        if (clazz) {
          this.form.clazzName = clazz.name;
          this.form.className = clazz.name;
          this.form.classId = clazz.id;
          if (clazz.enterYear) this.form.enterYear = clazz.enterYear;
        }
        if (this.form.studyNumber != null) this.form.studentCode = this.form.studyNumber;
        this.onSubjectChange(this.form.subjectId);
        this.onTitleChange(this.form.titleId);
      },
      findCascader(vm) {
        if (!vm || !vm.$children) return null;
        for (let i = 0; i < vm.$children.length; i++) {
          const child = vm.$children[i];
          if (child && child.$options && child.$options.name === 'ElCascader') return child;
          const found = this.findCascader(child);
          if (found) return found;
        }
        return null;
      },
      enterEdit() {
        this.editSnapshot = JSON.parse(JSON.stringify(this.form));
        this.isEditing = true;
        this.$nextTick(() => {
          if (this.hasArea) this.fetchSchools();
          if (this.form.schoolId) this.fetchClazz();
        });
      },
      cancelEdit() {
        if (this.editSnapshot) {
          this.form = JSON.parse(JSON.stringify(this.editSnapshot));
        }
        this.isEditing = false;
        this.$nextTick(() => {
          this.$refs.form && this.$refs.form.clearValidate();
        });
      },
      onSubmit() {
        this.$refs.form.validate((valid) => {
          if (valid) this.commitForm();
        });
      },
      async commitForm() {
        this.syncFormBeforeSave();
        const changed = getChangedIdentityFields(this.form, this.editSnapshot || {});
        if (Object.keys(changed).length) {
          try {
            const params = { id: this.form.id };
            if (changed.name != null) params.name = changed.name;
            if (changed.phone != null) {
              const n = Number(changed.phone);
              params.phone = Number.isNaN(n) ? changed.phone : n;
            }
            const checkRes = await this.$Api.My.checkUserExist(params);
            if (isUserExistResponse(checkRes)) {
              this.$notify.error({ title: '错误', message: '该用户已存在' });
              return;
            }
          } catch (e) {
            console.warn('check_exist 调用失败，继续保存（后端 update_info 仍有查重兜底）', e);
          }
        }
        const payload = buildFixInfoPayload(this.form, this.roles);
        this.saving = true;
        try {
          const resData = await this.$Api.My.getfixinfo(payload);
          const msg = (resData && resData.msg) || (resData.data && resData.data.msg) || '';
          if (isUserExistResponse(resData) || (typeof msg === 'string' && msg.indexOf('已存在') > -1)) {
            this.$notify.error({ title: '错误', message: '该用户已存在' });
            return;
          }
          if (this.isOk(resData)) {
            this.$notify({ title: '成功', message: '个人信息修改成功', type: 'success' });
            this.isEditing = false;
            await this.userinfo();
          } else {
            this.$notify.error({
              title: '错误',
              message: msg || '保存失败',
            });
          }
        } catch (e) {
          this.$notify.error({ title: '错误', message: '保存失败' });
        } finally {
          this.saving = false;
        }
      },
      async userinfo() {
        const resData = await this.$Api.Home.userInfo();
        const user = this.unwrapUser(resData);
        this.$store.commit('getUserInfo', user);
        this.applyUser(user);
      },
      applyUser(raw) {
        const form = normalizeProfileForm(raw || {});
        form.areaPath = parseAreaPathFromForm(form);
        if (!form.areaId && form.areaPath.length) {
          form.areaId = form.areaPath[form.areaPath.length - 1];
        }
        if (form.areaPath.length) {
          form.province = form.areaPath[0];
          form.city = form.areaPath[1];
        }
        this.form = form;
      },
      async getTitleList() {
        const resData = await this.$Api.My.getTitle();
        this.titleList = unwrapList(resData);
      },
      async getSubjectList() {
        const resData = await this.$Api.My.getSubject();
        this.subjectList = unwrapList(resData);
      },
      async init() {
        await this.userinfo();
        this.getTitleList();
        this.getSubjectList();
      },
    },
    created() {
      // lazyLoad 闭包需要组件实例
      const that = this;
      this.cascaderProps.lazyLoad = (node, resolve) => {
        that.loadRegionNodes(node, resolve);
      };
    },
    mounted() {
      this.init();
    },
    activated() {
      this.init();
    },
  };
</script>

<style lang="less" scoped>
  .info-setting {
    border-radius: 10px;
  }

  .profile-card {
    background: #fff;
    border-radius: 10px;
    box-shadow: 0 2px 10px rgba(36, 155, 253, 0.06);
    overflow: hidden;
  }

  .profile-banner {
    display: flex;
    align-items: flex-start;
    padding: 24px 32px;
    background: linear-gradient(180deg, #f8fbff 0%, #f0f6ff 100%);
    border-bottom: 1px solid #e8f0fa;
  }

  .banner-main {
    flex: 1;
    min-width: 0;
    margin-left: 20px;
  }

  .banner-title-row {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
  }

  .title {
    margin: 0;
    font-size: 20px;
    line-height: 28px;
    font-weight: 600;
    color: #1f2d3d;
  }

  .role-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .role-tag {
    display: inline-block;
    padding: 2px 10px;
    border-radius: 12px;
    background: #e8f2ff;
    color: #3a7bd5;
    font-size: 12px;
    line-height: 20px;
  }

  .intro-block {
    margin-top: 12px;
    min-height: 72px;

    .intro-label {
      display: block;
      font-size: 13px;
      color: #606266;
      margin-bottom: 6px;
    }

    /deep/ .el-textarea__inner {
      height: 72px;
      min-height: 72px;
    }
  }

  .intro-readonly {
    height: 72px;
    overflow-y: auto;
    font-size: 13px;
    line-height: 22px;
    color: #606266;
    padding-right: 4px;

    &::-webkit-scrollbar {
      width: 4px;
    }

    &::-webkit-scrollbar-thumb {
      background: #c0c4cc;
      border-radius: 2px;
    }
  }

  .banner-actions {
    flex-shrink: 0;
    margin-left: 16px;

    /deep/ .el-button--primary {
      background-color: #508ef9;
      border-color: #508ef9;
    }

    /deep/ .el-button--default {
      color: #508ef9;
      border-color: #508ef9;
    }
  }

  .form-section {
    padding: 24px 32px 40px;
  }

  .readonly-box {
    height: 32px;
    line-height: 32px;
    padding: 0 12px;
    background: #f8fafc;
    border: 1px solid #eef2f7;
    border-radius: 6px;
    color: #606266;
    font-size: 14px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .form-section /deep/ .el-input__inner,
  .form-section /deep/ .el-input {
    height: 32px;
    line-height: 32px;
  }

  .form-section /deep/ .el-input__inner {
    height: 32px;
  }

  .form-section /deep/ .el-cascader,
  .form-section /deep/ .el-select {
    width: 100%;
  }

  @media screen and (max-width: 900px) {
    .field-col {
      width: 100% !important;
      max-width: 100%;
      flex: 0 0 100%;
    }

    .profile-banner {
      flex-wrap: wrap;
    }

    .banner-actions {
      width: 100%;
      margin: 12px 0 0;
      text-align: right;
    }
  }
</style>
